// =============================================================
// Vertical variant configs for the demo dashboard toggle.
// Shows white-label flexibility: Enrichment / Aesthetic / Dental.
// Public site stays enrichment-only; these appear ONLY in-app.
// =============================================================

import {
  LEADS, STAGES, BRANCHES, PROGRAMMES, STAFF, KPIS, FUNNEL, DAILY_SERIES,
  FOLLOW_UP_QUEUE, SOURCE_PERFORMANCE, BRANCH_PERFORMANCE, STAFF_PERFORMANCE,
} from "./mockData";

function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rand = mulberry32(88231);
const pick = (arr) => arr[Math.floor(rand() * arr.length)];
const randInt = (min, max) => Math.floor(rand() * (max - min + 1)) + min;

// ---------------- Aesthetic Clinic ----------------

const AES_STAGES = [
  { id: "new_lead", name: "New Lead" },
  { id: "contacted", name: "Contacted" },
  { id: "qualified", name: "Qualified" },
  { id: "consult_booked", name: "Consult Booked" },
  { id: "consult_attended", name: "Consult Attended" },
  { id: "treatment_proposed", name: "Treatment Proposed" },
  { id: "treatment_booked", name: "Treatment Booked" },
  { id: "treatment_done", name: "Treatment Completed" },
  { id: "rebook_nurture", name: "Rebook Nurture" },
  { id: "review_request", name: "Review Request" },
  { id: "lost", name: "Lost" },
];
const AES_SERVICES = ["HIFU", "Pico Laser", "Fillers", "Botox"];
const AES_BRANCHES = ["Orchard", "Raffles Place"];
const AES_NAMES = ["Cheryl Lim", "Vanessa Tan", "Grace Ho", "Melissa Ong", "Fiona Ng", "Rachel Koh", "Amanda Teo", "Serena Goh", "Nadia Rahman", "Priya Menon", "Jasmine Lee", "Elaine Chua", "Denise Wong", "Stephanie Sim", "Michelle Yeo", "Clara Chen"];
const AES_WEIGHTS = [["new_lead", 12], ["contacted", 11], ["qualified", 9], ["consult_booked", 11], ["consult_attended", 8], ["treatment_proposed", 7], ["treatment_booked", 9], ["treatment_done", 7], ["rebook_nurture", 4], ["review_request", 3], ["lost", 3]];
const aesPool = AES_WEIGHTS.flatMap(([id, w]) => Array(w).fill(id));

const AES_LEADS = aesPool.map((stage, i) => {
  const service = AES_SERVICES[i % AES_SERVICES.length];
  const stageName = AES_STAGES.find((s) => s.id === stage).name;
  return {
    contact_id: `AC-${2000 + i}`,
    parent_name: pick(AES_NAMES),
    child_name: null,
    programme_name: service,
    branch_name: AES_BRANCHES[i % 2],
    source: pick(["Instagram Ads", "Website", "WhatsApp", "Referral", "Google Search"]),
    stage, stage_name: stageName,
    assigned_user_name: pick(["Dr Chia", "Nurse Amirah", "Consultant Jolene"]),
    monetary_value: service === "HIFU" ? 1800 : service === "Pico Laser" ? 450 : 800,
    first_response_mins: randInt(3, 200),
    next_action: stage === "consult_booked" ? "Send consult reminder" : stage === "treatment_proposed" ? "Follow up on quote" : "Respond on WhatsApp",
    last_activity_date: new Date(Date.now() - randInt(0, 6) * 864e5).toISOString(),
    created_date: new Date(Date.now() - randInt(0, 29) * 864e5).toISOString(),
  };
});

// ---------------- Dental Recall ----------------

const DEN_STAGES = [
  { id: "recall_due", name: "Recall Due" },
  { id: "contacted", name: "Contacted" },
  { id: "booked", name: "Booked" },
  { id: "attended", name: "Attended" },
  { id: "treatment_proposed", name: "Treatment Proposed" },
  { id: "treatment_accepted", name: "Treatment Accepted" },
  { id: "review_request", name: "Review Request" },
  { id: "dormant", name: "Dormant" },
];
const DEN_SEGMENTS = ["Hygiene overdue", "Implant follow-up", "Cosmetic consult nurture"];
const DEN_CLINICS = ["City Dental — Raffles", "City Dental — Novena"];
const DEN_SURNAMES = ["Tan", "Lim", "Lee", "Ng", "Wong", "Chen", "Kumar", "Hassan", "Pereira", "Goh", "Ong", "Chua"];
const DEN_WEIGHTS = [["recall_due", 62], ["contacted", 38], ["booked", 34], ["attended", 26], ["treatment_proposed", 18], ["treatment_accepted", 14], ["review_request", 8], ["dormant", 8]];
const denPool = DEN_WEIGHTS.flatMap(([id, w]) => Array(w).fill(id));

const DEN_LEADS = denPool.map((stage, i) => {
  const seg = DEN_SEGMENTS[i % 3];
  return {
    contact_id: `DC-${4000 + i}`,
    parent_name: `${pick(["Mr", "Mrs", "Ms", "Mdm"])} ${pick(DEN_SURNAMES)}`,
    child_name: null,
    programme_name: seg,
    branch_name: DEN_CLINICS[i % 2],
    source: pick(["Existing patient base", "Website", "Referral"]),
    stage, stage_name: DEN_STAGES.find((s) => s.id === stage).name,
    assigned_user_name: pick(["Dr Yap", "Dr Fernandez", "Hygienist Kelly"]),
    monetary_value: seg === "Implant follow-up" ? 3200 : seg === "Cosmetic consult nurture" ? 1400 : 180,
    first_response_mins: randInt(5, 300),
    next_action: stage === "recall_due" ? "Send recall WhatsApp" : stage === "treatment_proposed" ? "Chase treatment plan" : "Confirm appointment",
    last_activity_date: new Date(Date.now() - randInt(0, 10) * 864e5).toISOString(),
    created_date: new Date(Date.now() - randInt(0, 60) * 864e5).toISOString(),
  };
});

const aesCount = (id) => AES_LEADS.filter((l) => l.stage === id).length;
const denCount = (id) => DEN_LEADS.filter((l) => l.stage === id).length;

// ---------------- Export configs ----------------

export const VERTICALS = {
  enrichment: {
    id: "enrichment",
    label: "Enrichment Centre",
    headline: "Parent Conversion OS",
    subheadline: "Bright Minds Learning Lab — Singapore Maths & Enrichment Centre",
    entityLabel: "Parent",
    programmeLabel: "Programme",
    branchLabel: "Branch",
    branchLabelPlural: "Branches",
    stages: STAGES,
    leads: LEADS,
    branches: BRANCHES.map((b) => b.name),
    programmes: PROGRAMMES.map((p) => p.name),
    staff: STAFF,
    dailySeries: DAILY_SERIES,
    funnel: FUNNEL,
    followUpQueue: FOLLOW_UP_QUEUE,
    sourcePerformance: SOURCE_PERFORMANCE,
    branchPerformance: BRANCH_PERFORMANCE,
    staffPerformance: STAFF_PERFORMANCE,
    kpis: [
      { id: "new-enquiries", label: "New Enquiries", value: KPIS.new_enquiries, delta: "+12% vs last 30d", up: true },
      { id: "trials-booked", label: "Trials Booked", value: KPIS.trials_booked, delta: "+8% vs last 30d", up: true },
      { id: "show-rate", label: "Trial Show Rate", value: `${KPIS.show_rate}%`, delta: "+5 pts", up: true },
      { id: "enrolments", label: "Enrolments", value: KPIS.enrolments, delta: "+3 this week", up: true },
      { id: "re-enrol", label: "Re-Enrolment Nudges", value: KPIS.re_enrolment_nudges, delta: "5 closing this week", up: null },
      { id: "referrals", label: "Referrals", value: KPIS.referrals, delta: "+2 vs last 30d", up: true },
    ],
  },
  aesthetic: {
    id: "aesthetic",
    label: "Aesthetic Clinic",
    headline: "Consult-to-Treatment Conversion OS",
    subheadline: "Orchard Aesthetic Clinic",
    entityLabel: "Client",
    programmeLabel: "Service Line",
    branchLabel: "Clinic",
    branchLabelPlural: "Clinics",
    stages: AES_STAGES,
    leads: AES_LEADS,
    branches: AES_BRANCHES,
    programmes: AES_SERVICES,
    staff: [
      { id: "chia", name: "Dr Chia", role: "Medical Director", initials: "DC" },
      { id: "amirah", name: "Nurse Amirah", role: "Clinical Nurse", initials: "NA" },
      { id: "jolene", name: "Consultant Jolene", role: "Treatment Consultant", initials: "CJ" },
    ],
    dailySeries: DAILY_SERIES.map((d) => ({ ...d, enquiries: Math.max(1, d.enquiries - 2), trials: Math.max(1, d.trials - 1) })),
    funnel: [
      { stage: "Lead", count: AES_LEADS.length },
      { stage: "Consult Booked", count: aesCount("consult_booked") + aesCount("consult_attended") + aesCount("treatment_proposed") + aesCount("treatment_booked") + aesCount("treatment_done") },
      { stage: "Consult Attended", count: aesCount("consult_attended") + aesCount("treatment_proposed") + aesCount("treatment_booked") + aesCount("treatment_done") },
      { stage: "Treatment Booked", count: aesCount("treatment_booked") + aesCount("treatment_done") },
    ],
    followUpQueue: AES_LEADS.filter((l) => ["treatment_proposed", "consult_attended", "rebook_nurture"].includes(l.stage)).slice(0, 8).map((l) => ({ lead: l, reason: l.stage === "treatment_proposed" ? "Quote sent — no decision" : l.stage === "rebook_nurture" ? "Due for rebooking nudge" : "Consult done — propose plan", priority: l.stage === "treatment_proposed" ? "high" : "medium", due: "Today" })),
    sourcePerformance: ["Instagram Ads", "Website", "WhatsApp", "Referral", "Google Search"].map((s) => ({ source: s, enquiries: AES_LEADS.filter((l) => l.source === s).length, trials: AES_LEADS.filter((l) => l.source === s && ["consult_booked", "consult_attended", "treatment_booked", "treatment_done"].includes(l.stage)).length, enrolments: AES_LEADS.filter((l) => l.source === s && ["treatment_booked", "treatment_done"].includes(l.stage)).length })),
    branchPerformance: AES_BRANCHES.map((b) => ({ branch: b, enquiries: AES_LEADS.filter((l) => l.branch_name === b).length, trials: AES_LEADS.filter((l) => l.branch_name === b && ["consult_attended", "treatment_booked", "treatment_done"].includes(l.stage)).length, enrolments: AES_LEADS.filter((l) => l.branch_name === b && ["treatment_booked", "treatment_done"].includes(l.stage)).length, avg_response: 24 })),
    staffPerformance: ["Dr Chia", "Nurse Amirah", "Consultant Jolene"].map((s) => ({ staff: s, role: "Clinical", leads: AES_LEADS.filter((l) => l.assigned_user_name === s).length, trials: randInt(6, 16), enrolments: randInt(3, 9), avg_response: randInt(10, 60), sla_met: randInt(78, 98) })),
    kpis: [
      { id: "new-leads", label: "New Leads", value: AES_LEADS.length, delta: "+9% vs last 30d", up: true },
      { id: "consults", label: "Consults Booked", value: aesCount("consult_booked") + aesCount("consult_attended"), delta: "+6% vs last 30d", up: true },
      { id: "show-rate", label: "Consult Show Rate", value: "82%", delta: "+3 pts", up: true },
      { id: "treatments", label: "Treatments Booked", value: aesCount("treatment_booked") + aesCount("treatment_done"), delta: "+4 this week", up: true },
      { id: "response", label: "Avg First Response", value: "14 min", delta: "-6 min", up: true },
      { id: "rebook", label: "Rebook Rate", value: "41%", delta: "+5 pts", up: true },
    ],
  },
  dental: {
    id: "dental",
    label: "Dental Recall",
    headline: "Recall & Reactivation OS",
    subheadline: "City Dental Group",
    entityLabel: "Patient",
    programmeLabel: "Segment",
    branchLabel: "Clinic",
    branchLabelPlural: "Clinics",
    stages: DEN_STAGES,
    leads: DEN_LEADS,
    branches: DEN_CLINICS,
    programmes: DEN_SEGMENTS,
    staff: [
      { id: "yap", name: "Dr Yap", role: "Principal Dentist", initials: "DY" },
      { id: "fernandez", name: "Dr Fernandez", role: "Associate Dentist", initials: "DF" },
      { id: "kelly", name: "Hygienist Kelly", role: "Hygienist", initials: "HK" },
    ],
    dailySeries: DAILY_SERIES.map((d) => ({ ...d, enquiries: d.enquiries + 3, trials: d.trials + 2 })),
    funnel: [
      { stage: "Recall Due", count: DEN_LEADS.length },
      { stage: "Contacted", count: DEN_LEADS.length - denCount("recall_due") - denCount("dormant") },
      { stage: "Booked", count: denCount("booked") + denCount("attended") + denCount("treatment_proposed") + denCount("treatment_accepted") },
      { stage: "Attended", count: denCount("attended") + denCount("treatment_proposed") + denCount("treatment_accepted") },
      { stage: "Treatment Accepted", count: denCount("treatment_accepted") },
    ],
    followUpQueue: DEN_LEADS.filter((l) => ["recall_due", "treatment_proposed"].includes(l.stage)).slice(0, 8).map((l) => ({ lead: l, reason: l.stage === "recall_due" ? `${l.programme_name} — overdue` : "Treatment plan pending", priority: l.stage === "treatment_proposed" ? "high" : "medium", due: "Today" })),
    sourcePerformance: ["Existing patient base", "Website", "Referral"].map((s) => ({ source: s, enquiries: DEN_LEADS.filter((l) => l.source === s).length, trials: DEN_LEADS.filter((l) => l.source === s && ["booked", "attended", "treatment_accepted"].includes(l.stage)).length, enrolments: DEN_LEADS.filter((l) => l.source === s && l.stage === "treatment_accepted").length })),
    branchPerformance: DEN_CLINICS.map((b) => ({ branch: b, enquiries: DEN_LEADS.filter((l) => l.branch_name === b).length, trials: DEN_LEADS.filter((l) => l.branch_name === b && ["booked", "attended"].includes(l.stage)).length, enrolments: DEN_LEADS.filter((l) => l.branch_name === b && l.stage === "treatment_accepted").length, avg_response: 38 })),
    staffPerformance: ["Dr Yap", "Dr Fernandez", "Hygienist Kelly"].map((s) => ({ staff: s, role: "Clinical", leads: DEN_LEADS.filter((l) => l.assigned_user_name === s).length, trials: randInt(10, 24), enrolments: randInt(4, 12), avg_response: randInt(15, 70), sla_met: randInt(72, 95) })),
    kpis: [
      { id: "recall-due", label: "Recall Due", value: denCount("recall_due"), delta: "62 this month", up: null },
      { id: "booked", label: "Booked", value: denCount("booked"), delta: "+11% vs last 30d", up: true },
      { id: "show-rate", label: "Show Rate", value: "79%", delta: "+2 pts", up: true },
      { id: "pending", label: "Treatment Pending", value: denCount("treatment_proposed"), delta: "$28k pipeline", up: null },
      { id: "response", label: "Avg First Response", value: "22 min", delta: "-9 min", up: true },
      { id: "reviews", label: "Reviews Sent", value: denCount("review_request") + 14, delta: "+6 vs last 30d", up: true },
    ],
  },
};

export const VERTICAL_IDS = Object.keys(VERTICALS);
